import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { pushDataLayerEvent } from '../utils/analytics';
import './Pricing.css';


// Function to calculate one-time price in dollars for given credits
const calculateOneTimePrice = (credits) => {
  if (credits <= 1200) {
    return 50 + (credits - 300) * (100 / 900);
  } else if (credits <= 4000) {
    return 150 + (credits - 1200) * (150 / 2800);
  } else if (credits <= 10000) {
    return 300 + (credits - 4000) * (200 / 6000);
  } else {
    return 500 + (credits - 10000) * 0.03;
  }
};

const getPriceNumber = (price) => Number(String(price).replace(/[^0-9.]/g, ''));

const getCreditCheckoutEcommerce = ({ itemId, itemName, credits, paymentType, value }) => ({
  value,
  currency: 'USD',
  items: [
    {
      item_id: itemId,
      item_name: itemName,
      item_category: 'Credits',
      price: value,
      quantity: 1,
      credits,
      payment_type: paymentType,
    },
  ],
});

function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeFaq, setActiveFaq] = useState(null);
  const [paymentType, setPaymentType] = useState('oneTime');
  const [customCredits, setCustomCredits] = useState(10000);
  const [error, setError] = useState('');

  const toggleFaq = (index) => {
    if (activeFaq === index) {
      setActiveFaq(null);
    } else {
      setActiveFaq(index);
    }
  };

  const pricingPlans = [
    {
      packageId: "starter",
      name: "Starter Pack",
      credits: "300",
      oneTimePrice: "$50",
      monthlyPrice: "$40",
      bestFor: "Small businesses starting out with address management",
      features: [
        "No expiration date",
        "Use for any HubSpot portal",
        "HubSpot integration"
      ]
    },
    {
      packageId: "growth",
      name: "Growth Pack",
      credits: "1,200",
      oneTimePrice: "$150",
      monthlyPrice: "$120",
      bestFor: "Growing businesses with regular contact management",
      features: [
        "No expiration date",
        "Use for any HubSpot portal",
        "HubSpot integration"
      ]
    },
    {
      packageId: "scale",
      name: "Scale Pack",
      credits: "4,000",
      oneTimePrice: "$300",
      monthlyPrice: "$240",
      bestFor: "Established businesses with high volume needs",
      features: [
        "No expiration date",
        "Use for any HubSpot portal",
        "HubSpot integration"
      ],
      popular: true
    },
    {
      packageId: "enterprise",
      name: "Enterprise",
      credits: "10,000",
      oneTimePrice: "$500",
      monthlyPrice: "$400",
      bestFor: "Large organizations with specific requirements",
      features: [
        "No expiration date",
        "Use for any HubSpot portal",
        "HubSpot integration"
      ]
    }
  ];

  const faqs = [
    {
      question: "What is a credit?",
      answer: "A successful standard lookup consumes 1 credit. A successful international lookup consumes 2 credits and uses the record's postal code and country to populate your configured address fields. Failed lookups and attempts that make no changes do not consume credits."
    },
    {
      question: "Do credits expire?",
      answer: "No, your credits never expire. Use them at your own pace without worrying about timelines."
    },
    {
      question: "Can I purchase a custom amount of credits?",
      answer: "Yes, if you need a specific number of credits not covered by our standard packages, please contact us for a custom quote."
    },
    {
      question: "What postal codes are supported?",
      answer: "ZipCode AutoFill supports global ZIP and postal codes. Enable the experimental International lookup option in your portal configuration and make sure the record has its country populated before adding or changing its postal code. Coverage and available fields vary by location."
    },
    {
      question: "Can I backfill existing contacts?",
      answer: "Yes, if you have access to HubSpot workflows, you can use our service to backfill address information for existing contacts that already have ZIP codes."
    },
    {
      question: "How do monthly subscriptions work?",
      answer: "Monthly subscriptions automatically add credits to your account each month. You can cancel anytime through your account settings. Credits from subscriptions never expire, just like one-time purchases."
    }
  ];

  const handlePaymentTypeChange = (type) => {
    console.log('Pricing page - Changing payment type to:', type); // Debug log
    setPaymentType(type);
  };

  const handlePurchase = async (packageId) => {
    if (!user) {
      navigate('/register');
      return;
    }
    setError('');
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/create-checkout-session`, {
        packageId,
        paymentType
      }, {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });

      const selectedPlan = pricingPlans.find((plan) => plan.packageId === packageId);
      const value = getPriceNumber(paymentType === 'monthly' ? selectedPlan.monthlyPrice : selectedPlan.oneTimePrice);

      pushDataLayerEvent({
        event: 'begin_checkout',
        userEmail: user.email,
        checkout_session_id: response.data.sessionId,
        package_id: packageId,
        payment_type: paymentType,
        ecommerce: getCreditCheckoutEcommerce({
          itemId: `credits_${packageId}_${paymentType}`,
          itemName: `${selectedPlan.name} ${paymentType === 'monthly' ? 'Monthly' : 'One-Time'}`,
          credits: Number(selectedPlan.credits.replace(/,/g, '')),
          paymentType,
          value,
        }),
      });
      
      if (!response.data.url) {
        throw new Error('Stripe Checkout URL was not returned');
      }
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Purchase error:', error);
      setError('Failed to initiate purchase');
    }
  };

  const handleCustomPurchase = async (credits) => {
    if (!user) {
      navigate('/register');
      return;
    }
    setError('');
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/create-checkout-session`, {
        customCredits: credits,
        paymentType
      }, {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });

      const value = Number((paymentType === 'monthly' ? calculateOneTimePrice(credits) * 0.8 : calculateOneTimePrice(credits)).toFixed(2));

      pushDataLayerEvent({
        event: 'begin_checkout',
        userEmail: user.email,
        checkout_session_id: response.data.sessionId,
        package_id: 'custom',
        payment_type: paymentType,
        ecommerce: getCreditCheckoutEcommerce({
          itemId: `credits_custom_${credits}_${paymentType}`,
          itemName: `${credits.toLocaleString()} Credits ${paymentType === 'monthly' ? 'Monthly' : 'One-Time'}`,
          credits,
          paymentType,
          value,
        }),
      });
      
      if (!response.data.url) {
        throw new Error('Stripe Checkout URL was not returned');
      }
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Custom purchase error:', error);
      setError('Failed to initiate custom purchase');
    }
  };

  return (
    <div className="pricing-container">
      {/* Auto-Top Up Message */}
      <div className="auto-topup-message">
        <div className="auto-topup-icon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
          </svg>
        </div>
        <div className="auto-topup-content">
          <h3>Auto-Top Up now available!</h3>
          <p>Use the <Link to="/support" className="auto-topup-link">Support Form</Link> to set up!</p>
        </div>
      </div>

      <div className="pricing-header">
        <div className="pricing-shapes">
          <div className="pricing-shape shape1"></div>
          <div className="pricing-shape shape2"></div>
          <div className="pricing-shape shape3"></div>
        </div>
        <h1>Simple, Transparent Pricing</h1>
        <p className="pricing-intro">
          Pay only for what you use with our credit-based system. Choose between one-time purchases or save 20% when you choose a monthly subscription.
        </p>
        
        {/* Payment Type Toggle */}
        <div className="payment-type-toggle">
          <button 
            className={`toggle-btn ${paymentType === 'oneTime' ? 'active' : ''}`}
            onClick={() => handlePaymentTypeChange('oneTime')}
          >
            One-Time Purchase
          </button>
          <button 
            className={`toggle-btn ${paymentType === 'monthly' ? 'active' : ''}`}
            onClick={() => handlePaymentTypeChange('monthly')}
          >
            Monthly Subscription
          </button>
        </div>
      </div>

      <div className="pricing-plans">
        {pricingPlans.map((plan, index) => {
          const currentPrice = paymentType === 'monthly' ? plan.monthlyPrice : plan.oneTimePrice;
          const period = paymentType === 'monthly' ? 'per month' : 'one-time payment';
          const creditsLabel = paymentType === 'monthly' ? 'Credits per month' : 'Credits';
          
          return (
            <div key={index} className={`pricing-plan ${plan.popular ? 'popular' : ''}`}>
              {plan.popular && <div className="popular-badge">Most Popular</div>}
              <h2 className="plan-name">{plan.name}</h2>
              <div className="plan-price">
                <span className="price">{currentPrice}</span>
                <span className="period">{period}</span>
              </div>
              <div className="plan-credits">
                <span className="credits-amount">{plan.credits}</span>
                <span className="credits-label">{creditsLabel}</span>
              </div>
              <p className="plan-description">{plan.bestFor}</p>
              <ul className="plan-features">
                {plan.features.map((feature, i) => (
                  <li key={i}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>
              <div className="plan-action">
                {user ? (
                  <button 
                    onClick={() => handlePurchase(plan.packageId)} 
                    className="plan-button"
                  >
                    {paymentType === 'monthly' ? 'Subscribe' : 'Purchase'}
                  </button>
                ) : (
                  <Link to="/register" className="plan-button">
                    Get Started
                  </Link>
                )}
              </div>
            </div>
          );
        })}
      </div>
<br />
      <div className="custom-slider-container">
        <div className="custom-slider-content">
          <div className="custom-slider-left">
            <h2>Custom Credit Amount</h2>
            <input
              type="range"
              min="10000"
              max="100000"
              step="1000"
              value={customCredits}
              onChange={(e) => setCustomCredits(parseInt(e.target.value))}
            />
            <div className="custom-credits-display">
              <span>{customCredits.toLocaleString()} Credits</span>
              <span>
                ${ (paymentType === 'monthly' ? calculateOneTimePrice(customCredits) * 0.8 : calculateOneTimePrice(customCredits)).toFixed(2) }
                {paymentType === 'monthly' ? ' per month' : ' one-time'}
              </span>
            </div>
          </div>
          <div className="custom-slider-right">
            {user ? (
              <button 
                onClick={() => handleCustomPurchase(customCredits)} 
                className="plan-button"
              >
                {paymentType === 'monthly' ? 'Subscribe' : 'Purchase'}
              </button>
            ) : (
              <Link to="/register" className="plan-button">
                Get Started
              </Link>
            )}
          </div>
        </div>
      </div>

      <div className="enterprise-cta">
        <h2>Need a Custom Solution?</h2>
        <p>For larger volumes or specific requirements, contact our sales team for a tailored package.</p>
        <a href="mailto:info@zipcodeautofill.com" className="enterprise-button">
          Contact Us
        </a>
      </div>

      <div className="pricing-faq">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-list">
          {faqs.map((faq, index) => (
            <div 
              key={index} 
              className={`faq-item ${activeFaq === index ? 'active' : ''}`}
              onClick={() => toggleFaq(index)}
            >
              <div className="faq-question">
                <h3>{faq.question}</h3>
                <div className="faq-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12" className="vertical-line"></line>
                  </svg>
                </div>
              </div>
              <div className="faq-answer">
                <p>{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="pricing-guarantee">
        <div className="guarantee-icon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
          </svg>
        </div>
        <div className="guarantee-content">
          <h3>Our Guarantee</h3>
          <p>
            Credits are only consumed for successful lookups. If a ZIP code cannot be found or is invalid, 
            no credit is deducted from your account. If you ever have a credit dispute, please reach out to our Support team and we will take care of you.
          </p>
        </div>
      </div>

      {error && <p className="error">{error}</p>}
    </div>
  );
}

export default Pricing;
